<!--
  AI:
  Status: "confirmed"
  Type: Component
  SubType: CrudIntegration
  Reason: Top-down manual adjustment of an existing cp.ProvisionContract and/or one of its
          cp.ProvisionContractAgent rows. Both mvs-crud-object forms run in CAPTURE ONLY mode
          (hideDefaultActions) - the component owns the save gate and the write sequence itself.
          This file contains NO hand-built form controls.
-->
<p-dialog
        header="Manuell anpassen"
        [visible]="visible"
        (visibleChange)="handleVisibleChange($event)"
        (onShow)="handleShow()"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [closable]="!state.saveBusy"
        [style]="{width: '62rem'}"
        [breakpoints]="{'960px': '92vw'}"
        styleClass="cp-provision-contract-adjust-wizard">

    @if (!initialized) {

        <p-skeleton height="18rem"></p-skeleton>

    } @else if (isTargetMissing()) {

        <div class="flex flex-column align-items-center gap-2 p-5 surface-ground border-round-lg">
            <i class="fa-regular fa-circle-info text-3xl text-color-secondary"></i>
            <span class="font-semibold">Keine Zeile zum Anpassen</span>
            <span class="text-color-secondary">
                Öffnen Sie die manuelle Anpassung aus einer Provisionszeile oder einem Provisionssatz
                heraus.
            </span>
        </div>

    } @else if (completed) {

        <!-- ══════════════════════ Ergebnis ══════════════════════ -->
        <div class="flex flex-column gap-4">

            <div class="flex align-items-center gap-3 p-3 surface-ground border-round-lg">
                <i class="fa-solid fa-circle-check text-2xl text-green-500"></i>
                <div class="flex flex-column">
                    <span class="font-semibold">Änderungen übernommen</span>
                    <span class="text-color-secondary text-sm">{{ getScopeLabel() }}</span>
                </div>
            </div>

            <ng-container [ngTemplateOutlet]="deferredPanel"></ng-container>
        </div>

    } @else {

        <div class="cp-wizard-steps">

            @if (!canAgentChange) {
                <p-message severity="warn" styleClass="w-full">
                    <span>
                        Der Server hat den Vorgang verweigert: Ihnen fehlt das Recht
                        <strong>cp_agentChange</strong>.
                    </span>
                </p-message>
            }

            <!-- ══════════════════════ Schritt 1: Umfang ══════════════════════ -->
            @if (isStepDecided('scope')) {

                <div class="cp-wizard-step">
                    <div class="cp-step-header">
                        <div class="cp-step-number">
                            @if (isScopeFixed()) {
                                <i class="fa-solid fa-check"></i>
                            } @else {
                                {{ getStepNumber('scope') }}
                            }
                        </div>

                        <div class="cp-step-title">
                            <h3>Umfang</h3>
                            <span class="cp-step-subtitle">Was in diesem Durchlauf angepasst wird</span>
                        </div>

                        <div class="cp-step-status cp-step-completed">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>{{ isScopeFixed() ? 'Vorgegeben' : 'Gewählt' }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content">
                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-list-check"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">Umfang</span>
                                <span class="cp-result-value">{{ getScopeLabel() }}</span>
                            </div>

                            @if (canChangeStep('scope')) {
                                <p-button
                                        label="Ändern"
                                        icon="fa-regular fa-pen"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="handleChangeStep('scope')">
                                </p-button>
                            }
                        </div>
                    </div>
                </div>
            }

            @if (isStepActive('scope')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('scope') }}</div>

                        <div class="cp-step-title">
                            <h3>Umfang</h3>
                            <span class="cp-step-subtitle">
                                Provisionszeile, Provisionssatz oder beides anpassen
                            </span>
                        </div>

                        <div class="cp-step-status cp-step-in-progress">
                            <span>Schritt {{ getStepNumber('scope') }} von {{ state.steps.length }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        <p-message severity="info" styleClass="w-full">
                            <span>
                                Es wird noch nichts gespeichert. Die Änderungen werden erst im letzten
                                Schritt gemeinsam übernommen.
                            </span>
                        </p-message>

                        <div class="grid">
                            @if (hasContract()) {
                                <div class="col-12 md:col-4">
                                    <div class="cp-scope-tile flex flex-column gap-2 p-3 border-1 border-round-lg h-full"
                                         [class.cp-scope-tile-selected]="isScopeSelected('contract')"
                                         (click)="handleSelectScope('contract')">
                                        <i class="fa-regular fa-file-contract text-xl text-primary"></i>
                                        <span class="font-semibold">Nur Provisionszeile</span>
                                        <span class="text-sm text-color-secondary">
                                            Relevanz-Überschreibung, Zeitraum und Storno der Zeitscheibe.
                                        </span>
                                    </div>
                                </div>
                            }

                            @if (hasAgent()) {
                                <div class="col-12 md:col-4">
                                    <div class="cp-scope-tile flex flex-column gap-2 p-3 border-1 border-round-lg h-full"
                                         [class.cp-scope-tile-selected]="isScopeSelected('agent')"
                                         (click)="handleSelectScope('agent')">
                                        <i class="fa-regular fa-user-tie text-xl text-primary"></i>
                                        <span class="font-semibold">Nur Provisionssatz</span>
                                        <span class="text-sm text-color-secondary">
                                            Beträge, angewandte Parameter und Betragsüberschreibung.
                                        </span>
                                    </div>
                                </div>
                            }

                            @if (hasContract() && hasAgent()) {
                                <div class="col-12 md:col-4">
                                    <div class="cp-scope-tile flex flex-column gap-2 p-3 border-1 border-round-lg h-full"
                                         [class.cp-scope-tile-selected]="isScopeSelected('both')"
                                         (click)="handleSelectScope('both')">
                                        <i class="fa-regular fa-layer-group text-xl text-primary"></i>
                                        <span class="font-semibold">Beides</span>
                                        <span class="text-sm text-color-secondary">
                                            Zuerst die Zeile, danach der Satz — in einem Durchlauf.
                                        </span>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }

            <!-- ══════════════════════ Schritt: Provisionszeile ══════════════════════ -->
            @if (hasStep('contract') && isStepDecided('contract')) {

                <div class="cp-wizard-step">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('contract') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionszeile</h3>
                            <span class="cp-step-subtitle">Manuelle Anpassung der Zeitscheibe</span>
                        </div>

                        <div class="cp-step-status cp-step-completed">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>Erfasst</span>
                        </div>
                    </div>

                    <div class="cp-step-content">
                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-file-contract"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">Provisionszeile</span>
                                <span class="cp-result-value">Änderungen erfasst</span>
                            </div>

                            @if (canChangeStep('contract')) {
                                <p-button
                                        label="Ändern"
                                        icon="fa-regular fa-pen"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="handleChangeStep('contract')">
                                </p-button>
                            }
                        </div>
                    </div>
                </div>
            }

            @if (isStepActive('contract')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('contract') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionszeile</h3>
                            <span class="cp-step-subtitle">
                                Vertrag, Provisionsart und Ereignisdatum sind unveränderlich
                            </span>
                        </div>

                        <div class="cp-step-status cp-step-in-progress">
                            <span>Schritt {{ getStepNumber('contract') }} von {{ state.steps.length }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        <p-message severity="info" styleClass="w-full">
                            <span>
                                Das Ereignisdatum ist einmalig vergeben und bleibt unverändert — es ist
                                der Stichtag jeder Konfigurationsauflösung dieser Zeile. Die vom
                                Berechnungslauf gefüllten Felder werden hier nicht angeboten.
                            </span>
                        </p-message>

                        @if (isOutsideRunSelection()) {
                            <p-message severity="warn" styleClass="w-full">
                                <span>{{ getSelectionWarning() }}</span>
                            </p-message>
                        }

                        <!-- AI: CRUD Object Component for cp.ProvisionContract, CAPTURE ONLY -->
                        <mvs-crud-object
                                [objectType]="'cp.ProvisionContract'"
                                [objectId]="provisionContractId"
                                [formControlOverwrite]="contractFormControlOverwrite"
                                [importObjectContextDto]="formImportObjectContext"
                                [hideDefaultActions]="true"
                                (onFormChange)="handleContractFormChange($event)"
                                (onFormSubmitStateChange)="handleContractSubmitStateChange($event)">
                        </mvs-crud-object>
                    </div>
                </div>
            }

            <!-- ══════════════════════ Schritt: Provisionssatz ══════════════════════ -->
            @if (hasStep('agent') && isStepDecided('agent')) {

                <div class="cp-wizard-step">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('agent') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionssatz</h3>
                            <span class="cp-step-subtitle">Manuelle Anpassung des Satzes</span>
                        </div>

                        <div class="cp-step-status cp-step-completed">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>Erfasst</span>
                        </div>
                    </div>

                    <div class="cp-step-content">
                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-user-tie"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">Provisionssatz</span>
                                <span class="cp-result-value">Änderungen erfasst</span>
                            </div>

                            @if (canChangeStep('agent')) {
                                <p-button
                                        label="Ändern"
                                        icon="fa-regular fa-pen"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="handleChangeStep('agent')">
                                </p-button>
                            }
                        </div>
                    </div>
                </div>
            }

            @if (isStepActive('agent')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('agent') }}</div>

                        <div class="cp-step-title">
                            <h3>Provisionssatz</h3>
                            <span class="cp-step-subtitle">
                                Beträge und angewandte Parameter des Satzes
                            </span>
                        </div>

                        <div class="cp-step-status cp-step-in-progress">
                            <span>Schritt {{ getStepNumber('agent') }} von {{ state.steps.length }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        <p-message severity="info" styleClass="w-full">
                            <span>
                                Der Berater wird hier nicht gewechselt: Ein Beraterwechsel storniert und
                                bucht die betroffenen Beträge um. Nutzen Sie dafür die Aktion
                                <strong>Berater wechseln</strong>. Eine Betragsüberschreibung benötigt
                                zwingend einen Grund.
                            </span>
                        </p-message>

                        <!-- AI: CRUD Object Component for cp.ProvisionContractAgent, CAPTURE ONLY -->
                        <mvs-crud-object
                                [objectType]="'cp.ProvisionContractAgent'"
                                [objectId]="provisionContractAgentId"
                                [formControlOverwrite]="agentFormControlOverwrite"
                                [importObjectContextDto]="formImportObjectContext"
                                [hideDefaultActions]="true"
                                (onFormChange)="handleAgentFormChange($event)"
                                (onFormSubmitStateChange)="handleAgentSubmitStateChange($event)">
                        </mvs-crud-object>
                    </div>
                </div>
            }

            <!-- ══════════════════════ Schritt: Prüfen und übernehmen ══════════════════════ -->
            @if (isStepActive('summary')) {

                <div class="cp-wizard-step cp-wizard-step-active">
                    <div class="cp-step-header">
                        <div class="cp-step-number">{{ getStepNumber('summary') }}</div>

                        <div class="cp-step-title">
                            <h3>Prüfen und übernehmen</h3>
                            <span class="cp-step-subtitle">
                                Was jetzt geschrieben wird und was der nächste Lauf neu berechnet
                            </span>
                        </div>

                        <div class="cp-step-status cp-step-in-progress">
                            <span>Schritt {{ getStepNumber('summary') }} von {{ state.steps.length }}</span>
                        </div>
                    </div>

                    <div class="cp-step-content flex flex-column gap-3">

                        <div class="cp-step-result">
                            <div class="cp-result-icon">
                                <i class="fa-regular fa-list-check"></i>
                            </div>

                            <div class="cp-result-details">
                                <span class="cp-result-label">Umfang</span>
                                <span class="cp-result-value">{{ getScopeLabel() }}</span>
                            </div>
                        </div>

                        <ng-container [ngTemplateOutlet]="deferredPanel"></ng-container>
                    </div>
                </div>
            }
        </div>

        @if (state.saveError) {
            <div class="flex flex-column gap-2 p-3 surface-ground border-round-lg mt-3">
                <p-message severity="error" [text]="state.saveError" styleClass="w-full"></p-message>

                @if (state.saveHint) {
                    <span class="text-sm text-color-secondary">{{ state.saveHint }}</span>
                }

                @if (state.hasPartialResult()) {
                    <span class="text-sm text-color-secondary">
                        Bereits übernommene Änderungen bleiben erhalten. „Erneut versuchen“ setzt genau
                        an der fehlgeschlagenen Stelle fort und schreibt nichts doppelt.
                    </span>

                    <span class="text-sm text-color-secondary">
                        Bei „Abbrechen“ werden sie nicht zurückgenommen — die Zeile trägt dann bereits
                        den neuen Stand, der Satz noch nicht.
                    </span>
                }
            </div>
        }
    }

    <!-- ══════════════════════ Abschluss: die beiden Feldgruppen ══════════════════════ -->
    <!--
      Same split as in the create wizard, and for the same reason - the two groups are not the same
      promise:

        DEFERRED - four nullable fields of the Provisionszeile that are never asked. The pending
                   sentence stands, because the next run really does compute them.
        CAPTURED  - eligibility plus the five liability fields. Their columns are NOT NULL without a
                   default, so the value on the row was stated by someone; the wizard offers them for
                   correction and shows what stands. The pending sentence must NOT appear over one of
                   them.

      Neither group ever renders a blank cell.
    -->
    <ng-template #deferredPanel>
        <div class="flex flex-column gap-3">

            <!-- ── Was der nächste Lauf ergänzt ── -->
            <div class="cp-deferred-panel flex flex-column gap-3">

                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-calculator text-color-secondary"></i>
                    <span class="font-semibold">Was der nächste Berechnungslauf ergänzt</span>
                </div>

                <!-- BT-12: explanation once here, per field only the factual statement. -->
                <span class="text-sm text-color-secondary">
                    {{ deferredExplanation }}
                    Bei der manuellen Anpassung wird nichts gerechnet; was bereits gefüllt ist, steht
                    mit seinem Wert.
                </span>

                @if (adjustsContract() && isOutsideRunSelection()) {
                    <p-message severity="warn" styleClass="w-full">
                        <span>{{ getSelectionWarning() }}</span>
                    </p-message>
                }

                @if (adjustsContract()) {
                    <div class="flex flex-column gap-2">
                        <h4>Provisionszeile</h4>

                        @for (field of getDeferredContractFields(); track field.key) {
                            <div class="cp-deferred-row">
                                <span class="cp-deferred-label">{{ field.label }}</span>

                                @if (field.captured) {
                                    <span class="cp-deferred-value">{{ field.value }}</span>
                                } @else {
                                    <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                                }

                                <p-tag severity="info" [value]="field.hint"></p-tag>
                            </div>
                        }
                    </div>
                }

                @if (adjustsAgent()) {
                    <div class="flex flex-column gap-2">
                        <h4>Provisionssatz</h4>

                        @for (field of getDeferredAgentFields(); track field.key) {
                            <div class="cp-deferred-row">
                                <span class="cp-deferred-label">{{ field.label }}</span>

                                @if (field.captured) {
                                    <span class="cp-deferred-value">{{ field.value }}</span>
                                } @else {
                                    <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                                }

                                <p-tag severity="info" [value]="field.hint"></p-tag>
                            </div>
                        }
                    </div>
                }
            </div>

            <!-- ── Ihre Angaben ── -->
            <div class="cp-captured-panel flex flex-column gap-3">

                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-keyboard text-color-secondary"></i>
                    <span class="font-semibold">Ihre Angaben</span>
                </div>

                <span class="text-sm text-color-secondary">
                    Diese Felder sind Pflichtangaben der Zeile beziehungsweise des Satzes und werden
                    nicht gerechnet, sondern von Ihnen gesetzt.
                </span>

                @if (adjustsContract()) {
                    <div class="flex flex-column gap-2">
                        <h4>Provisionszeile</h4>

                        @for (field of getCapturedContractFields(); track field.key) {
                            <div class="cp-deferred-row">
                                <span class="cp-deferred-label">{{ field.label }}</span>

                                @if (field.captured) {
                                    <span class="cp-deferred-value">{{ field.value }}</span>
                                } @else {
                                    <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                                }

                                <p-tag severity="success" [value]="field.hint"></p-tag>
                            </div>
                        }
                    </div>
                }

                @if (adjustsAgent()) {
                    <div class="flex flex-column gap-2">
                        <h4>Provisionssatz</h4>

                        @for (field of getCapturedAgentFields(); track field.key) {
                            <div class="cp-deferred-row">
                                <span class="cp-deferred-label">{{ field.label }}</span>

                                @if (field.captured) {
                                    <span class="cp-deferred-value">{{ field.value }}</span>
                                } @else {
                                    <span class="cp-deferred-value cp-deferred-pending">{{ field.hint }}</span>
                                }

                                <p-tag severity="success" [value]="field.hint"></p-tag>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    </ng-template>

    <ng-template pTemplate="footer">

        @if (completed || isTargetMissing()) {

            <p-button
                    label="Schließen"
                    icon="fa-regular fa-xmark"
                    severity="primary"
                    (onClick)="handleClose()">
            </p-button>

        } @else {

            <p-button
                    label="Abbrechen"
                    icon="fa-regular fa-xmark"
                    severity="secondary"
                    [text]="true"
                    [disabled]="state.saveBusy"
                    (onClick)="handleCancel()">
            </p-button>

            @if (canGoBack()) {
                <p-button
                        label="Zurück"
                        icon="fa-regular fa-arrow-left"
                        severity="secondary"
                        [outlined]="true"
                        (onClick)="handleBack()">
                </p-button>
            }

            @if (!state.isLastStep()) {
                <p-button
                        label="Weiter"
                        icon="fa-regular fa-arrow-right"
                        iconPos="right"
                        severity="primary"
                        [disabled]="!canGoForward()"
                        (onClick)="handleForward()">
                </p-button>
            } @else if (state.saveError) {
                <p-button
                        label="Erneut versuchen"
                        icon="fa-regular fa-rotate-right"
                        severity="primary"
                        [loading]="state.saveBusy"
                        [disabled]="state.saveBusy"
                        (onClick)="handleRetrySave()">
                </p-button>
            } @else {
                <p-button
                        label="Übernehmen"
                        icon="fa-regular fa-check"
                        severity="primary"
                        [loading]="state.saveBusy"
                        [disabled]="!canSave()"
                        [pTooltip]="getSubmitTooltip()"
                        tooltipPosition="top"
                        (onClick)="handleSave()">
                </p-button>
            }
        }
    </ng-template>

</p-dialog>
